import { io } from 'socket.io-client'
import { useAuthStore } from '@/stores/auth'

class SocketService {
  constructor() {
    this.socket = null
    this.listeners = new Map()
  }

  connect() {
    const authStore = useAuthStore()
    
    // If socket exists and is connected, just re-authenticate
    if (this.socket?.connected) {
      this.authenticateSocket()
      return
    }

    // If socket exists but not connected, it will auto-reconnect
    if (this.socket) {
      return
    }

    // Remove /api suffix from the base URL for Socket.IO connection
    const rawUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'
    const apiBase = rawUrl.replace(/\/api\/?$/, '') // Remove /api or /api/ at end only
    
    console.log('[Socket] Raw URL:', rawUrl)
    console.log('[Socket] Connecting to:', apiBase)

    this.socket = io(apiBase, {
      auth: {
        token: authStore.token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    })

    this.socket.on('connect', () => {
      this.authenticateSocket()
    })

    this.socket.on('disconnect', () => {})

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message)
    })
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.listeners.clear()
    }
  }

  on(event, callback) {
    if (!this.socket) {
      this.connect()
    }

    this.socket.on(event, callback)

    // Store listener for cleanup
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
  }

  off(event, callback) {
    if (!this.socket) return

    this.socket.off(event, callback)

    // Remove from stored listeners
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event)
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
      if (callbacks.length === 0) {
        this.listeners.delete(event)
      }
    }
  }

  emit(event, data) {
    if (!this.socket) {
      this.connect()
    }
    this.socket.emit(event, data)
  }

  authenticateSocket() {
    const authStore = useAuthStore()
    if (this.socket?.connected && authStore.user?.id) {
      this.socket.emit('auth', authStore.user.id)
    }
  }
}

export default new SocketService()
